import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  Divider 
} from '@mui/material';
import { ref, get } from 'firebase/database';
import { database } from '../config/firebase';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';

interface Manager {
  phonenumber: string;
  username: string;
  password: string;
  grade: string;
  appname: string;
  block: string;
  initdate: string;
  referralcode: string;
}

interface DepositData {
  phonenumber: string;
  username: string;
  currentDeposit: number;
  lastUpdateDate: string;
}

interface DepositLog {
  timestamp: string;
  type: 'deposit' | 'withdraw' | 'cancel';
  amount: number;
  date: string;
  reason?: string;
  balance?: number;
}

interface AutocallDepositManagerProps {
  open: boolean;
  onClose: () => void;
  selectedManager: Manager | null;
  currentUser: any;
}

const AutocallDepositManager: React.FC<AutocallDepositManagerProps> = ({
  open,
  onClose,
  selectedManager,
  currentUser
}) => {
  const [depositLogs, setDepositLogs] = useState<DepositLog[]>([]);
  const [depositData, setDepositData] = useState<DepositData | null>(null);

  useEffect(() => {
    if (open && selectedManager) {
      fetchDepositData(selectedManager.referralcode);
    } else if (open && currentUser) {
      // localmanager로 로그인한 경우 본인의 referralcode 사용
      fetchDepositData(currentUser.referralcode);
    }
  }, [open, selectedManager, currentUser]);

  const fetchDepositData = async (referralcode: string) => {
    try {
      // deposit 테이블에서 현재 예치금 정보 가져오기
      const depositRef = ref(database, `deposit/${referralcode}`);
      const depositSnapshot = await get(depositRef);
      
      if (depositSnapshot.exists()) {
        const data = depositSnapshot.val();
        setDepositData(data);
      } else {
        // 예치금 데이터가 없는 경우 0으로 초기화
        setDepositData({
          phonenumber: referralcode,
          username: selectedManager?.username || currentUser?.username || '',
          currentDeposit: 0,
          lastUpdateDate: ''
        });
      }

      // depositlogs 테이블에서 로그 가져오기      
      const logsRef = ref(database, `depositlogs/${referralcode}`);      
      const logsSnapshot = await get(logsRef);
      
      if (logsSnapshot.exists()) {
        const logsData = logsSnapshot.val();
        const logs = Object.entries(logsData).map(([timestamp, logData]: [string, any]) => ({
          timestamp,
          type: logData.type,
          amount: logData.amount,
          date: logData.date,
          reason: logData.reason || '',
          balance: logData.balance || 0
        })).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
        setDepositLogs(logs);
      } else {
        setDepositLogs([]);
      }
    } catch (error) {
      console.error('예치금 데이터 조회 실패:', error);
      setDepositData({
        phonenumber: referralcode,
        username: selectedManager?.username || currentUser?.username || '',
        currentDeposit: 0,
        lastUpdateDate: ''
      });
      setDepositLogs([]);
    }
  };



  const handleClose = () => {
    setDepositLogs([]);
    setDepositData(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>연장승인 히스토리</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            전화번호: {formatPhoneNumber(selectedManager?.phonenumber || currentUser?.id || '')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            이름: {selectedManager?.username || currentUser?.username || ''}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
            현재 예치금: {depositData?.currentDeposit?.toLocaleString() || '0'}원
          </Typography>
          {depositData?.lastUpdateDate && (
            <Typography variant="body2" color="text.secondary">
              마지막 업데이트: {depositData.lastUpdateDate}
            </Typography>
          )}
        </Box>        

        <Box>          
          <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {depositLogs.length > 0 ? (
              <List dense>
                {depositLogs.map((log, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={
                        <Box>
                          {/* 모바일 뷰 - 세로 배치 */}
                          <Box sx={{ 
                            display: { xs: 'flex', md: 'none' }, 
                            flexDirection: 'column',
                            gap: 1
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">{log.date}</Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: log.type === 'deposit' ? '#4caf50' : '#f44336',
                                  marginLeft: 1
                                }}
                              >
                                {log.type === 'deposit'
                                  ? '입금'
                                  : log.type === 'cancel'
                                    ? '요청취소'
                                    : '연장'}
                              </Typography>                              
                            </Box>
                          </Box>

                          {/* PC 뷰 - 가로 배치 */}
                          <Box sx={{ 
                            display: { xs: 'none', md: 'flex' }, 
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Box sx={{ width: '140px' }}>{log.date}</Box>
                            <Box sx={{ 
                              width: '60px', 
                              textAlign: 'center', 
                              color: log.type === 'deposit' ? '#4caf50' : '#f44336'
                            }}>
                              {log.type === 'deposit'
                                  ? '입금'
                                  : log.type === 'cancel'
                                    ? '요청취소'
                                    : '연장'}
                            </Box>                            
                          </Box>
                        </Box>
                      }
                      secondary={
                        log.type !== 'deposit' && log.reason ? (
                          <Box sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem' }}>
                            사유: {log.reason}
                          </Box>
                        ) : null
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                히스토리가 없습니다.
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutocallDepositManager;
